// src/components/requests/RequestForm.jsx
import React, { useState, useCallback } from 'react';

export const AppView = {
  HOME: 'HOME',
  RESULTS: 'RESULTS',
  PROVIDER: 'PROVIDER',
  USER_DASHBOARD: 'USER_DASHBOARD',
};

export const TIMEFRAME_OPTIONS = [
  { value: 'asap', label: 'Urgent (ASAP)' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'flexible', label: 'Flexible' },
];

export const PROVIDER_CATEGORIES = [
  "Home Improvement & Repair",
  "Cleaning Services",
  "Plumbing & HVAC",
  "Electrical",
  "Moving & Storage",
  "Web & Graphic Design",
  "Digital Marketing",
  "Tutoring & Education",
  "Events & Catering",
  "Personal Training & Wellness",
  "Legal & Financial",
  "Other"
];

const TABS = [
  { id: 'Home Services', label: 'Home Services' },
  { id: 'Professional', label: 'Professional' },
  { id: 'Creative', label: 'Creative' }
];

const RequestForm = ({ onSubmit, isSubmitting, isDarkMode, bookData, placeholder }) => {
  const [description, setDescription] = useState('');
  const [maxResponses, setMaxResponses] = useState(bookData?.requestsSettings?.defaultMaxResponses || 3);
  const [timeframe, setTimeframe] = useState(TIMEFRAME_OPTIONS[0]?.value || 'asap');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState(TABS[0].id);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!description.trim()) return;
    onSubmit({ description, maxResponses, timeframe, location, category });
  }, [description, maxResponses, timeframe, location, category, onSubmit]);

  const primaryColor = bookData?.styling?.primaryColor || '#6366f1';
  const accentColor = bookData?.styling?.accentColor || '#8b5cf6';

  return (
    <div className="w-full max-w-4xl mx-auto animate-fade-in-up flex flex-col items-center">
      
      {/* Hero Text - Hidden when passed from parent */}
      {!bookData?.title && (
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white mb-4">
            Get offers <span 
              className="text-transparent bg-clip-text bg-gradient-to-r"
              style={{
                backgroundImage: `linear-gradient(to right, ${primaryColor}, ${accentColor})`
              }}
            >
              from providers
            </span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Don't search. Request. Describe your request and let providers come to you.
          </p>
        </div>
      )}

      {/* Main Search Card */}
      <div className="w-full max-w-3xl">
        {/* Tabs */}
        <div className="flex justify-center mb-6">
          <div className={`inline-flex p-1 rounded-xl ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setCategory(tab.id)}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  category === tab.id
                    ? isDarkMode
                      ? 'bg-slate-700 text-white shadow-lg'
                      : 'bg-white text-slate-900 shadow-md'
                    : isDarkMode
                      ? 'text-slate-400 hover:text-slate-200'
                      : 'text-slate-600 hover:text-slate-900'
                }`}
                style={category === tab.id && !isDarkMode ? { 
                  backgroundColor: 'white',
                  color: primaryColor 
                } : {}}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit}>
          <div className={`rounded-2xl shadow-2xl border transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-slate-800/50 border-slate-700' 
              : 'bg-white'
          }`}
          style={ !isDarkMode ? { 
                  border: primaryColor
                } : {}}
          >
            
            {/* Main Textarea */}
            <div className="p-3 sm:p-4">
              <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}>
                What do you need?
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={placeholder || "E.g., Plumber to fix leaking bathroom sink, preferably tomorrow morning"}
                rows={3}
                className={`w-full px-0 py-2 border-none focus:ring-0 outline-none resize-none text-md placeholder-slate-400 transition-colors ${
                  isDarkMode 
                    ? 'bg-transparent text-white placeholder-slate-500' 
                    : 'bg-transparent text-slate-900 placeholder-slate-400'
                }`}
                required
              />
            </div>

            {/* Divider */}
            <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}></div>

            {/* Grid Inputs */}
            <div className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-12 gap-4">
              
              {/* Max Responses */}
              <div className="sm:col-span-3 relative">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={maxResponses}
                  onChange={(e) => setMaxResponses(Number(e.target.value))}
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent outline-none transition-all text-sm ${
                    isDarkMode
                      ? 'bg-slate-900/50 border-slate-700 text-white placeholder-slate-500 focus:ring-indigo-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500'
                  }`}
                  style={{ '--tw-ring-color': primaryColor }}
                />
                <label className={`absolute -top-2 left-2 px-1 text-[10px] font-bold uppercase tracking-wider ${
                  isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-400'
                }`}>
                  Max Offers
                </label>
              </div>

              {/* Location */}
              <div className="sm:col-span-5 relative">
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="City or Area"
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent outline-none transition-all text-sm ${
                    isDarkMode
                      ? 'bg-slate-900/50 border-slate-700 text-white placeholder-slate-500 focus:ring-indigo-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500'
                  }`}
                  style={{ '--tw-ring-color': primaryColor }}
                />
                <label className={`absolute -top-2 left-2 px-1 text-[10px] font-bold uppercase tracking-wider ${
                  isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-400'
                }`}>
                  Location
                </label>
              </div>

              {/* Timeframe */}
              <div className="sm:col-span-4 relative">
                <select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent outline-none text-sm appearance-none cursor-pointer transition-all ${
                    isDarkMode
                      ? 'bg-slate-900/50 border-slate-700 text-white focus:ring-indigo-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-indigo-500'
                  }`}
                  style={{ '--tw-ring-color': primaryColor }}
                >
                  {TIMEFRAME_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <label className={`absolute -top-2 left-2 px-1 text-[10px] font-bold uppercase tracking-wider ${
                  isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-400'
                }`}>
                  Timeframe
                </label>
                <div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-500'
                }`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Submit Button */}
              <div className="sm:col-span-12 flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting || !description.trim()}
                  className={`px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center gap-2 ${
                    isSubmitting || !description.trim()
                      ? isDarkMode
                        ? 'bg-slate-700 cursor-not-allowed opacity-50'
                        : 'bg-slate-300 cursor-not-allowed opacity-50'
                      : 'shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                  }`}
                  style={!(isSubmitting || !description.trim()) ? { 
                    backgroundColor: primaryColor,
                    boxShadow: `0 10px 25px -5px ${primaryColor}40`
                  } : {}}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>Get Offers</span>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Footer Suggestions */}
        <div className="mt-6 flex flex-col items-center">
          <div className={`flex items-center gap-2 text-sm ${
            isDarkMode ? 'text-slate-400' : 'text-slate-500'
          }`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span>Verified providers only</span>
          </div>
          <div className={`mt-3 flex flex-wrap justify-center gap-2 text-xs ${
            isDarkMode ? 'text-slate-500' : 'text-slate-400'
          }`}>
            <span>Try:</span>
            <button 
              onClick={() => setDescription("Plumber for emergency leak")}
              className="hover:opacity-70 transition-opacity"
              style={{ color: primaryColor }}
              type="button"
            >
              "Plumber for emergency leak"
            </button>
            <span>•</span>
            <button 
              onClick={() => setDescription("Yoga instructor")}
              className="hover:opacity-70 transition-opacity"
              style={{ color: primaryColor }}
              type="button"
            >
              "Yoga instructor"
            </button>
            <span>•</span>
            <button 
              onClick={() => setDescription("Web designer")}
              className="hover:opacity-70 transition-opacity"
              style={{ color: primaryColor }}
              type="button"
            >
              "Web designer"
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestForm;